import React from 'react';

import './SubscriptionPage.css';

import {
  CrownOutlined,
  FireOutlined,
  PercentageOutlined,
  RocketOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import TableIcon from '../../assets/Icons/TableIcon.svg?react';
import LimitIcon from '../../assets/Icons/LimitIcon.svg?react';
import MiningIcon from '../../assets/Icons/MiningIcon.svg?react';
import TopPlayersIcon from '../../assets/Icons/TopPlayersIcon.svg?react';
import SupportIcon from '../../assets/Icons/SupportIcon.svg?react';
import Flex from '../../components/Flex/Flex';
import Text from '../../components/Text/Text';

type PriceTier = {
  label: string;
  price: string;
  highlight?: boolean;
};

type PricingPlan = {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentClass: string;
  tiers: PriceTier[];
  details?: string[];
  featured?: boolean;
};

const PROMOS = [
  {
    icon: <FireOutlined />,
    title: '3 месяца',
    discount: '−10%',
    description: 'При покупке подписки на 3 месяца',
    accentClass: 'SubscriptionPage__promo--warm',
  },
  {
    icon: <RocketOutlined />,
    title: '6 месяцев',
    discount: '−25%',
    description: 'При покупке на 6 месяцев',
    accentClass: 'SubscriptionPage__promo--cool',
  },
] as const;

const PLANS: PricingPlan[] = [
  {
    id: 'networks',
    title: 'PS + WPN + 888 + CHICO',
    subtitle: 'Основные сети',
    icon: <TableIcon />,
    accentClass: 'SubscriptionPage__plan-icon--blue',
    featured: true,
    tiers: [
      { label: 'NL 10–50', price: '15 $ / мес' },
      { label: 'UNLIM', price: '30 $ / мес', highlight: true },
    ],
    details: ['PS (10–200, частично NL500)', '888 (10–5K)', 'WPN (10–600)', 'CHICO (10–500)'],
  },
  {
    id: 'coin',
    title: 'COIN',
    icon: <MiningIcon />,
    accentClass: 'SubscriptionPage__plan-icon--gold',
    tiers: [
      { label: 'NL 10–50', price: '15 $ / мес' },
      { label: 'NL 10–200', price: '20 $ / мес' },
      { label: 'UNLIM NL10–1K', price: '35 $ / мес', highlight: true },
    ],
  },
  {
    id: 'ipoker',
    title: 'iPoker',
    icon: <LimitIcon />,
    accentClass: 'SubscriptionPage__plan-icon--green',
    tiers: [
      { label: 'NL 10–50', price: '12 $ / мес' },
      { label: 'NL 10–200', price: '20 $ / мес' },
    ],
  },
  {
    id: 'all',
    title: 'Все румы сразу',
    subtitle: 'Максимальный охват',
    icon: <TopPlayersIcon />,
    accentClass: 'SubscriptionPage__plan-icon--purple',
    featured: true,
    tiers: [
      { label: 'NL 10–25', price: '12 $ / мес' },
      { label: 'NL 10–50', price: '20 $ / мес' },
      { label: 'NL 10–200', price: '35 $ / мес' },
      { label: 'NL UNLIM', price: '45 $ / мес', highlight: true },
    ],
  },
];

const PriceRow = ({ label, price, highlight }: PriceTier) => (
  <li
    className={`SubscriptionPage__price-row${highlight ? ' SubscriptionPage__price-row--highlight' : ''}`}
  >
    <span className="SubscriptionPage__price-label">{label}</span>
    <span className="SubscriptionPage__price-value">{price}</span>
  </li>
);

const PlanCard = ({ plan }: { plan: PricingPlan }) => (
  <article
    className={`SubscriptionPage__plan${plan.featured ? ' SubscriptionPage__plan--featured' : ''}`}
  >
    {plan.featured && (
      <span className="SubscriptionPage__plan-badge">
        <StarOutlined /> Популярно
      </span>
    )}
    <div className={`SubscriptionPage__plan-icon ${plan.accentClass}`}>{plan.icon}</div>
    <div className="SubscriptionPage__plan-head">
      <Text size={4} bold>
        {plan.title}
      </Text>
      {plan.subtitle && (
        <Text type="secondary" size={2}>
          {plan.subtitle}
        </Text>
      )}
    </div>
    <ul className="SubscriptionPage__price-list">
      {plan.tiers.map((tier) => (
        <PriceRow key={tier.label} {...tier} />
      ))}
    </ul>
    {plan.details && plan.details.length > 0 && (
      <div className="SubscriptionPage__plan-details">
        <Text type="secondary" size={1} bold>
          UNLIM включает лимиты
        </Text>
        <ul className="SubscriptionPage__details-list">
          {plan.details.map((line) => (
            <li key={line}>
              <Text type="tertiary" size={2}>
                {line}
              </Text>
            </li>
          ))}
        </ul>
      </div>
    )}
  </article>
);

const SubscriptionPage = React.memo(() => {
  return (
    <div className="PageShell SubscriptionPage">
      <header className="PageHero">
        <div className="PageHero__glow" aria-hidden />
        <Flex vertical gap={12} className="PageHero__content">
          <span className="PageHero__eyebrow">
            <CrownOutlined /> Fish Hunter
          </span>
          <Text size={6} bold>
            Подписка и тарифы
          </Text>
          <Text type="secondary" size={3}>
            Выберите пакет под свои лимиты и румы. Дольше подписка — больше выгода.
          </Text>
        </Flex>
        <div className="SubscriptionPage__hero-art" aria-hidden>
          <span className="SubscriptionPage__chip SubscriptionPage__chip--1" />
          <span className="SubscriptionPage__chip SubscriptionPage__chip--2" />
          <span className="SubscriptionPage__chip SubscriptionPage__chip--3" />
        </div>
      </header>

      <section className="SubscriptionPage__promos" aria-label="Акции">
        {PROMOS.map((promo) => (
          <div key={promo.title} className={`SubscriptionPage__promo ${promo.accentClass}`}>
            <span className="SubscriptionPage__promo-icon">{promo.icon}</span>
            <Flex vertical gap={6}>
              <Text type="secondary" size={2}>
                {promo.description}
              </Text>
              <Flex align="center" gap={12}>
                <Text size={4} bold>
                  {promo.title}
                </Text>
                <span className="SubscriptionPage__promo-discount">
                  <PercentageOutlined /> {promo.discount}
                </span>
              </Flex>
            </Flex>
          </div>
        ))}
      </section>

      <section className="PageSection">
        <div className="PageSection__head">
          <Text size={5} bold>
            Цены на подписку
          </Text>
          <Text type="secondary" size={3}>
            1 месяц · оплата через менеджера в Telegram
          </Text>
        </div>

        <div className="SubscriptionPage__plans">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </section>

      <section className="SurfaceCta SubscriptionPage__cta">
        <span className="SubscriptionPage__cta-icon">
          <SupportIcon />
        </span>
        <Flex vertical gap={8} flex>
          <Text size={4} bold>
            Готовы оформить подписку?
          </Text>
          <Text type="secondary">
            Для покупки обращайтесь напрямую к{' '}
            <Link
              className="SubscriptionPage__link"
              target="_blank"
              to="https://t.me/fishh_support"
            >
              менеджеру в Telegram
            </Link>
          </Text>
        </Flex>
      </section>

      <aside className="SurfaceNote SubscriptionPage__note">
        <Text type="secondary" size={2}>
          <strong className="SubscriptionPage__note-label">Важно:</strong> подписка на PS — для тех,
          кто катает и делится своими руками. Кто присылает базы на PS, получает доступ на PS
          бесплатно. Также действует 25% скидка на любую подписку вне акционных предложений.
        </Text>
      </aside>
    </div>
  );
});

export default SubscriptionPage;
